import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, DollarSign, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

interface FinancialAssistanceRecord {
  id: number;
  person_id: number;
  agency_id: number;
  type_id: number | null;
  amount: number;
  date: string | null;
  funder_id: number | null;
  staff_id: number | null;
  notes: string | null;
  created_at: string;
  type_name?: string;
  staff_first_name?: string;
  staff_last_name?: string;
}

interface FinancialAssistanceType {
  id: number;
  name: string;
}

interface IncomeRecord {
  id: number;
  person_id: number;
  source: string | null;
  amount: number | null;
  frequency: string | null;
  record_date: string | null;
}

interface FinancialAssistanceProps {
  personId: string;
}

export function FinancialAssistance({ personId }: FinancialAssistanceProps) {
  const queryClient = useQueryClient();
  const [showAssistForm, setShowAssistForm] = useState(false);
  const [editingAssist, setEditingAssist] = useState<FinancialAssistanceRecord | null>(null);
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [activeSection, setActiveSection] = useState<"assistance" | "income">("assistance");

  const { data: assistData, isLoading: loadingAssist } = useQuery<{ data: FinancialAssistanceRecord[] }>({
    queryKey: ["financialAssistance", personId],
    queryFn: () => api.get(`/clients/${personId}/financial-assistance`),
    enabled: !!personId,
  });

  const { data: typesData } = useQuery<{ data: FinancialAssistanceType[] }>({
    queryKey: ["financialAssistanceTypes"],
    queryFn: () => api.get("/admin/financial-assistance-types"),
  });

  const { data: incomeData, isLoading: loadingIncome } = useQuery<{ data: IncomeRecord[] }>({
    queryKey: ["incomeRecords", personId],
    queryFn: () => api.get(`/clients/${personId}/income`),
    enabled: !!personId,
  });

  const createAssistMutation = useMutation({
    mutationFn: (body: { typeId?: number; amount: number; date?: string; notes?: string }) =>
      api.post(`/clients/${personId}/financial-assistance`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financialAssistance", personId] });
      setShowAssistForm(false);
    },
  });

  const updateAssistMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number; typeId?: number; amount?: number; date?: string; notes?: string }) =>
      api.put(`/clients/${personId}/financial-assistance/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financialAssistance", personId] });
      setEditingAssist(null);
    },
  });

  const deleteAssistMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/clients/${personId}/financial-assistance/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["financialAssistance", personId] }),
  });

  const createIncomeMutation = useMutation({
    mutationFn: (body: { source?: string; amount?: number; frequency?: string; recordDate?: string }) =>
      api.post(`/clients/${personId}/income`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incomeRecords", personId] });
      setShowIncomeForm(false);
    },
  });

  const deleteIncomeMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/clients/${personId}/income/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["incomeRecords", personId] }),
  });

  const assistRecords = assistData?.data ?? [];
  const assistTypes = typesData?.data ?? [];
  const incomeRecords = incomeData?.data ?? [];
  const totalAssistance = assistRecords.reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="space-y-6">
      {/* Section tabs */}
      <div className="border-b">
        <nav className="-mb-px flex gap-4">
          {[
            { id: "assistance" as const, label: "Financial Assistance" },
            { id: "income" as const, label: "Income Records" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeSection === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSection === "assistance" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold">Financial Assistance</h3>
              <p className="text-sm text-muted-foreground">Total: ${totalAssistance.toFixed(2)}</p>
            </div>
            <Button size="sm" className="gap-2" onClick={() => setShowAssistForm(true)}>
              <Plus className="h-4 w-4" />
              Add Assistance
            </Button>
          </div>

          {showAssistForm && (
            <Card>
              <CardContent className="p-4">
                <AssistanceForm
                  types={assistTypes}
                  onSubmit={(d) => createAssistMutation.mutate(d)}
                  onCancel={() => setShowAssistForm(false)}
                  isLoading={createAssistMutation.isPending}
                />
              </CardContent>
            </Card>
          )}

          {editingAssist && (
            <Card>
              <CardContent className="p-4">
                <AssistanceForm
                  types={assistTypes}
                  initial={editingAssist}
                  onSubmit={(d) => updateAssistMutation.mutate({ id: editingAssist.id, ...d })}
                  onCancel={() => setEditingAssist(null)}
                  isLoading={updateAssistMutation.isPending}
                />
              </CardContent>
            </Card>
          )}

          {loadingAssist && <div className="h-20 animate-pulse rounded-lg border bg-muted" />}

          {!loadingAssist && assistRecords.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <DollarSign className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">No financial assistance records.</p>
              </CardContent>
            </Card>
          )}

          {assistRecords.map((r) => {
            const staffName = r.staff_first_name && r.staff_last_name
              ? `${r.staff_first_name} ${r.staff_last_name}` : null;
            return (
              <Card key={r.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-medium">
                        ${r.amount.toFixed(2)} - {r.type_name ?? "Untyped"}
                      </CardTitle>
                      <CardDescription className="mt-0.5">
                        {r.date ? new Date(r.date).toLocaleDateString() : "No date"}
                        {staffName && ` | Staff: ${staffName}`}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditingAssist(r)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => {
                        if (confirm("Delete this record?")) deleteAssistMutation.mutate(r.id);
                      }}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {r.notes && (
                  <CardContent>
                    <p className="text-xs text-muted-foreground">{r.notes}</p>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {activeSection === "income" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">Income Records</h3>
            <Button size="sm" className="gap-2" onClick={() => setShowIncomeForm(true)}>
              <Plus className="h-4 w-4" />
              Add Income
            </Button>
          </div>

          {showIncomeForm && (
            <Card>
              <CardContent className="p-4">
                <IncomeForm
                  onSubmit={(d) => createIncomeMutation.mutate(d)}
                  onCancel={() => setShowIncomeForm(false)}
                  isLoading={createIncomeMutation.isPending}
                />
              </CardContent>
            </Card>
          )}

          {loadingIncome && <div className="h-20 animate-pulse rounded-lg border bg-muted" />}

          {!loadingIncome && incomeRecords.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-sm text-muted-foreground">No income records.</p>
              </CardContent>
            </Card>
          )}

          {incomeRecords.map((r) => (
            <Card key={r.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">
                      {r.source ?? "Unknown source"} - ${(r.amount ?? 0).toFixed(2)}
                    </CardTitle>
                    <CardDescription className="mt-0.5">
                      {r.frequency && <span className="capitalize">{r.frequency}</span>}
                      {r.record_date && ` | ${new Date(r.record_date).toLocaleDateString()}`}
                    </CardDescription>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => {
                    if (confirm("Delete this income record?")) deleteIncomeMutation.mutate(r.id);
                  }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

interface AssistanceFormProps {
  types: FinancialAssistanceType[];
  initial?: FinancialAssistanceRecord;
  onSubmit: (data: { typeId?: number; amount: number; date?: string; notes?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function AssistanceForm({ types, initial, onSubmit, onCancel, isLoading }: AssistanceFormProps) {
  const [typeId, setTypeId] = useState(initial?.type_id ? String(initial.type_id) : "");
  const [amount, setAmount] = useState(initial?.amount ? String(initial.amount) : "");
  const [date, setDate] = useState(initial?.date ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Type</label>
          <select
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select type...</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <Input label="Amount ($)" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes..." />
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => onSubmit({
            typeId: typeId ? parseInt(typeId, 10) : undefined,
            amount: parseFloat(amount) || 0,
            date: date || undefined,
            notes: notes || undefined,
          })}
          loading={isLoading}
          disabled={!amount}
        >
          {initial ? "Update" : "Add Assistance"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

interface IncomeFormProps {
  onSubmit: (data: { source?: string; amount?: number; frequency?: string; recordDate?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function IncomeForm({ onSubmit, onCancel, isLoading }: IncomeFormProps) {
  const [source, setSource] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("");
  const [recordDate, setRecordDate] = useState("");

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Income Source" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Employment, SSI, etc." />
        <Input label="Amount ($)" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Frequency</label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select...</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-weekly</option>
            <option value="monthly">Monthly</option>
            <option value="annually">Annually</option>
            <option value="one_time">One Time</option>
          </select>
        </div>
        <Input label="Date" type="date" value={recordDate} onChange={(e) => setRecordDate(e.target.value)} />
      </div>
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => onSubmit({
            source: source || undefined,
            amount: amount ? parseFloat(amount) : undefined,
            frequency: frequency || undefined,
            recordDate: recordDate || undefined,
          })}
          loading={isLoading}
        >
          Add Income
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
